import type { Placement } from './types';
import {
  splitForSelection,
  countCreditForSelection,
  selectionHasOwner,
  type Selection,
} from './splits';
import { addDaysIso, addMonthsIso, todayIso } from './format';
import { bookedSuccessorIds, effectiveEndDate, isRunner, isForecastRelevant } from './runners';

/**
 * Forecast engine.
 *
 * The visible window is ALWAYS [viewDate, viewDate + 12 months]. Nothing
 * outside that window is rendered on the chart.
 *
 * The renewal slider operates on every contractor whose split goes to the
 * viewer AND who finishes between today and the end of the visible window.
 * That means when viewing 3 months ahead with a 12-month window, the
 * renewal pool spans the full 15 months from today through to the end of
 * the visible window — not just the 12 months on screen. Contractors who
 * finish before the view date but get renewed will simply continue past
 * the start of the window without showing as a finish event.
 *
 * Renewal logic — per the rules doc:
 *   "2.5 contractors renewing, so round up to 3 for whole people"
 * → renewCount = ceil(pool.length × renewalPct), dropCount = pool.length − renewCount
 *
 * The pool is the set of at-risk placements whose likelyToExtend is
 * null/unsure/not-discussed. "expected" → auto-renewed; "not-expected"
 * → auto-dropped. Drops come from the earliest-ending end of the pool first.
 *
 * A placement whose extension is already booked in Mercury (a record with
 * the same root code and a higher extension number) is not at risk at all —
 * its continuation is a real record that carries the book from its own
 * start date, so the original is neither pooled nor artificially extended.
 */

const EXTENSION_DAYS = 180; // ~6 months
const DAY_MS = 24 * 60 * 60 * 1000;

export interface ForecastInput {
  placements: Placement[];
  selection: Selection;
  today: string;
  viewDate: string;
  renewalPct: number; // 0..1
}

export interface ProjectedPlacement {
  placement: Placement;
  effectiveEnd: string;
  renewed: boolean;
  autoRenewed: boolean;
  dropped: boolean;
  autoDropped: boolean;
  inPool: boolean;
  /** A successor extension record already exists in Mercury. */
  extensionBooked: boolean;
  splitValue: number;
}

export interface ForecastEvent {
  date: string;
  placement: Placement;
  splitValue: number;
  kind: 'start' | 'finish';
  /** 'extended' for a finish at the post-renewal end date */
  note?: string;
}

export interface TimelinePoint {
  date: string;
  bookValue: number;
}

export interface ForecastOutput {
  projected: ProjectedPlacement[];
  /** Step-function anchor points: viewDate, every event date, viewEnd. */
  timeline: TimelinePoint[];
  /** Every starter/finisher inside the visible window. */
  events: ForecastEvent[];
  /** Book value at every Monday inside the visible window. Used for line tooltips. */
  mondaySnapshots: TimelinePoint[];
  bookValueToday: number;
  bookValueAtViewDate: number;
  runnersToday: number;
  runnersAtViewDate: number;
  /** Sum of placement-count credit (0.5/0.25 per role) for runners in view. */
  runnersBySplitAtViewDate: number;
  startingSoonCount: number;
  atRiskCount: number;
  poolCount: number;
  renewCount: number;
  dropCount: number;
}

function horizonEnd(viewDate: string): string {
  return addMonthsIso(viewDate, 12);
}

function bookValueOn(projected: ProjectedPlacement[], date: string): number {
  let total = 0;
  for (const pp of projected) {
    if (pp.splitValue <= 0) continue;
    if (pp.placement.startDate > date) continue;
    if (pp.effectiveEnd < date) continue;
    total += pp.splitValue;
  }
  return total;
}

function applyRenewalLogic(
  placements: Placement[],
  selection: Selection,
  successorIds: ReadonlySet<string>,
  today: string,
  viewEnd: string,
  renewalPct: number,
): { projected: ProjectedPlacement[]; stats: Pick<ForecastOutput, 'atRiskCount' | 'poolCount' | 'renewCount' | 'dropCount'> } {
  // At-risk = the viewer's placements ending anywhere between today and the
  // end of the visible window, excluding those whose extension is already a
  // booked record (their renewal has effectively happened in Mercury). The
  // slider therefore controls what happens to every renewable contractor up
  // to that horizon, regardless of whether the user is currently viewing
  // today or some date in the future.
  const atRisk = placements.filter((p) => {
    if (successorIds.has(p.id)) return false;
    if (splitForSelection(p, selection) <= 0) return false;
    const e = effectiveEndDate(p);
    return e >= today && e <= viewEnd;
  });

  const autoRenewedIds = new Set<string>();
  const autoDroppedIds = new Set<string>();
  const pool: Placement[] = [];
  for (const p of atRisk) {
    if (p.likelyToExtend === 'expected') autoRenewedIds.add(p.id);
    else if (p.likelyToExtend === 'not-expected') autoDroppedIds.add(p.id);
    else pool.push(p);
  }

  // Earliest-finishing drop first; renewals round UP per spec.
  pool.sort((a, b) => effectiveEndDate(a).localeCompare(effectiveEndDate(b)));
  const renewCount = Math.ceil(pool.length * renewalPct);
  const dropCount = pool.length - renewCount;
  const droppedFromPool = new Set(pool.slice(0, dropCount).map((p) => p.id));

  const projected: ProjectedPlacement[] = placements.map((p) => {
    const splitValue = splitForSelection(p, selection);
    const inPool = pool.some((q) => q.id === p.id);
    const autoRenewed = autoRenewedIds.has(p.id);
    const autoDropped = autoDroppedIds.has(p.id);
    const dropped = droppedFromPool.has(p.id);
    const renewed = inPool && !dropped;
    const extensionBooked = successorIds.has(p.id);

    const origEnd = effectiveEndDate(p);
    const effEnd = (autoRenewed || renewed) ? addDaysIso(origEnd, EXTENSION_DAYS) : origEnd;

    return {
      placement: p,
      effectiveEnd: effEnd,
      renewed,
      autoRenewed,
      dropped,
      autoDropped,
      inPool,
      extensionBooked,
      splitValue,
    };
  });

  return {
    projected,
    stats: {
      atRiskCount: atRisk.length,
      poolCount: pool.length,
      renewCount,
      dropCount,
    },
  };
}

function buildEvents(
  projected: ProjectedPlacement[],
  viewStart: string,
  viewEnd: string,
): ForecastEvent[] {
  const events: ForecastEvent[] = [];
  for (const pp of projected) {
    if (pp.splitValue <= 0) continue;
    const p = pp.placement;
    // Starter event: placement begins inside the window. An extension record
    // starting isn't a new starter — the contractor is already on site, the
    // book just continues.
    if (p.extensionNumber === 0 && p.startDate >= viewStart && p.startDate <= viewEnd) {
      events.push({ date: p.startDate, placement: p, splitValue: pp.splitValue, kind: 'start' });
    }
    // Finisher event: placement ends inside the window (post-renewal). A
    // record handing over to a booked extension is noted as extended rather
    // than a true finish.
    if (pp.effectiveEnd >= viewStart && pp.effectiveEnd <= viewEnd) {
      const note = (pp.renewed || pp.autoRenewed || pp.extensionBooked) ? 'extended' : undefined;
      events.push({ date: pp.effectiveEnd, placement: p, splitValue: pp.splitValue, kind: 'finish', note });
    }
  }
  events.sort((a, b) => a.date.localeCompare(b.date));
  return events;
}

/**
 * Step-function anchor points: every distinct date where book value could
 * change inside the visible window, plus the start and end of the window.
 * Drawing a stepAfter line through these gives an exact step function.
 */
function buildTimeline(
  projected: ProjectedPlacement[],
  events: ForecastEvent[],
  viewStart: string,
  viewEnd: string,
): TimelinePoint[] {
  const dates = new Set<string>([viewStart, viewEnd]);
  for (const e of events) dates.add(e.date);
  // Extension starts are not starter events, but the book still steps up
  // when they begin — anchor on every contributing placement's start date.
  for (const pp of projected) {
    if (pp.splitValue <= 0) continue;
    const s = pp.placement.startDate;
    if (s >= viewStart && s <= viewEnd) dates.add(s);
  }
  const sorted = [...dates].sort();
  return sorted.map((date) => ({ date, bookValue: bookValueOn(projected, date) }));
}

/** Book value snapshot at every Monday in the window — used for hover tooltips. */
function buildMondaySnapshots(
  projected: ProjectedPlacement[],
  viewStart: string,
  viewEnd: string,
): TimelinePoint[] {
  const result: TimelinePoint[] = [];
  let d = new Date(viewStart + 'T00:00:00Z');
  while (d.getUTCDay() !== 1) d = new Date(d.getTime() + DAY_MS);
  const endMs = new Date(viewEnd + 'T00:00:00Z').getTime();
  while (d.getTime() <= endMs) {
    const date = d.toISOString().split('T')[0];
    result.push({ date, bookValue: bookValueOn(projected, date) });
    d = new Date(d.getTime() + 7 * DAY_MS);
  }
  return result;
}

export function runForecast(input: ForecastInput): ForecastOutput {
  const relevant = input.placements.filter((p) => isForecastRelevant(p, input.today));
  const successorIds = bookedSuccessorIds(relevant);
  const viewEnd = horizonEnd(input.viewDate);

  const { projected, stats } = applyRenewalLogic(
    relevant,
    input.selection,
    successorIds,
    input.today,
    viewEnd,
    input.renewalPct,
  );

  const events = buildEvents(projected, input.viewDate, viewEnd);
  const timeline = buildTimeline(projected, events, input.viewDate, viewEnd);
  const mondaySnapshots = buildMondaySnapshots(projected, input.viewDate, viewEnd);

  return {
    projected,
    timeline,
    events,
    mondaySnapshots,
    bookValueToday: bookValueOn(projected, input.today),
    bookValueAtViewDate: bookValueOn(projected, input.viewDate),
    // Runners and Runners by Split count the SAME population — placements
    // where the selection holds a credited role (ownership, not fee value,
    // so £0-WGP bodies count in both) — which also matches the contractor
    // table. Count credit is at most 1.0 per placement, so Runners by Split
    // can never exceed Runners.
    runnersToday: projected.filter(
      (pp) => selectionHasOwner(pp.placement, input.selection) && isRunner(pp.placement, input.today),
    ).length,
    runnersAtViewDate: projected.filter(
      (pp) =>
        selectionHasOwner(pp.placement, input.selection) &&
        pp.placement.startDate <= input.viewDate &&
        pp.effectiveEnd >= input.viewDate,
    ).length,
    runnersBySplitAtViewDate: projected.reduce(
      (sum, pp) =>
        pp.placement.startDate <= input.viewDate && pp.effectiveEnd >= input.viewDate
          ? sum + countCreditForSelection(pp.placement, input.selection)
          : sum,
      0,
    ),
    startingSoonCount: projected.filter(
      (pp) =>
        pp.splitValue > 0 &&
        pp.placement.extensionNumber === 0 &&
        pp.placement.startDate >= input.viewDate &&
        pp.placement.startDate <= viewEnd,
    ).length,
    ...stats,
  };
}

/** Convenience: the sum of splits for current runners (today). */
export function totalBookToday(
  placements: Placement[],
  selection: Selection,
): { bookValue: number; runnerCount: number } {
  const today = todayIso();
  let total = 0;
  let count = 0;
  for (const p of placements) {
    if (isRunner(p, today)) {
      total += splitForSelection(p, selection);
      count += 1;
    }
  }
  return { bookValue: total, runnerCount: count };
}